<script lang="ts">
  import { onMount } from "svelte";
  import { type JSONObject } from "../../lib/dataform/dataFormLib.svelte";
  import Fieldset from "../../components/uiform/Fieldset.svelte";
  import SearchInput from "../../components/daisyform/SearchInput.svelte";
  import Plus from "~icons/ph/plus-circle-bold";
  import { scrollIntoView } from "../../lib/dom";

  interface Address {
    address_id: string | null;
    line1: string;
    line2: string;
    line3: string;
    line4: string;
    city: string;
    postcode: string;
    full_address: string;
  }

  let {
    confirm,
    required,
    autofocus = false
  } : {
    confirm : (_: JSONObject)=>any;
    required? : boolean;
    autofocus? : boolean;
  } = $props()

  let fieldset = $state<HTMLFieldSetElement>()
  let input = $state<SearchInput>()

  const UNKNOWN = Symbol("UNKNOWN")
  let value = $state("")
  let found = $state<Address[]>([])
  let error = $state<typeof UNKNOWN | string | null>(null)
  let selected = $state<Address | null>(null)

  function reset(options = {}){
    const state = {
      value: value,
      found: [],
      error: null,
      selected: null,
      ...options
    };
    ({value, found, error, selected} = state)
  }

  async function search(value: string){
    reset();
    try {
      const response = await fetch(`/postcode_addresses/${encodeURIComponent(value)}`);
      const data = await response.json();
      if (data.error) return error = data.error;
      const addresses: Address[] = data.addresses;
      if (addresses.length === 0) return error = UNKNOWN;
      if (addresses.length === 1) return select(addresses[0]);
      found = addresses;
      setTimeout(() => {
        const input = fieldset?.querySelector("input");
        if (!input) return;
        input.scrollIntoView({ behavior: "smooth", block: "nearest" });
        input.focus();
      }, 500);
    } catch {
      error = UNKNOWN;
    }
  }

  function select(address: Address) {
    confirm({
      address: address.full_address,
      line1: address.line1,
      line2: address.line2,
      line3: address.line3,
      line4: address.line4,
      city: address.city,
      postcode: address.postcode,
      source: "checkboard",
      source_id: address.address_id,
      source_record: JSON.stringify(address)
    })
    reset({value: ""})
    input?.focus()
  }

  function validator(value: string) {
    if(!value || value.trim().length < 5) return "Enter your full UK postcode";
    return "";
  }

  onMount(()=>{ autofocus && input?.focus() })
</script>
<div class="flex flex-col gap-2">
  <Fieldset class="mb-0">
    <SearchInput
      bind:this={input}
      bind:value
      {search}
      {validator}
      placeholder="Enter postcode"
      inputClass="[&:not(:placeholder-shown)]:uppercase"
      buttonClass="btn-primary text-base"
      required={required && !found.length}
      autocomplete="postal-code"
    />
  </Fieldset>
  {#if error}
    <p class="text-error" {@attach scrollIntoView({ behavior: "smooth", block: "center" })}>
      {#if error == UNKNOWN}
        Something unexpected went wrong. Please try again.
      {:else}
        {error}
      {/if}
    </p>
  {/if}
  {#if found.length}
    <Fieldset bind:fieldset class="gap-2">
      {#each found as address}
        {@const itsme = selected == address}
        <div class="relative">
          <input
            type="checkbox"
            class="btn {itsme ? "border-blue-600" : ""} bg-white border border-solid outline-none
                 focus:border-blue-600 block w-full h-auto min-h-0 text-left text-base leading-tight font-normal
                   p-4 pr-12 rounded-lg scroll-mb-24"
            onclick={()=>select(address)}
            disabled={!!selected}
            name="suggestions"
            aria-label={address.full_address}
            {required}
          />
           <Plus class="h-6 w-6 shrink-0 absolute right-4 top-4 text-blue-600 pointer-events-none" />
        </div>
      {/each}
    </Fieldset>
  {/if}
</div>
